import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, eur, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AgentStatistics } from "@/components/AgentStatistics";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { TrendingUp, ShoppingCart, Receipt, Trophy, Package, Download, Wallet, FileText } from "lucide-react";
import { t } from "@/lib/i18n";

const PRESETS = [
  { key: "today", label: t("Oggi") },
  { key: "7d", label: t("7 giorni") },
  { key: "30d", label: t("30 giorni") },
  { key: "90d", label: t("90 giorni") },
  { key: "custom", label: t("Personalizzato") },
];

function rangeFor(preset) {
  const end = new Date();
  const start = new Date();
  if (preset === "today") {
    // same day
  } else if (preset === "7d") start.setDate(end.getDate() - 6);
  else if (preset === "30d") start.setDate(end.getDate() - 29);
  else if (preset === "90d") start.setDate(end.getDate() - 89);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

export default function Statistics() {
  const { user } = useAuth();
  return user?.role === "agent" ? <AgentStatistics /> : <CompanyStatistics />;
}

function CompanyStatistics() {
  const { user, activeCompanyId } = useAuth();
  const isCompany = user?.role === "company";
  const [preset, setPreset] = useState("30d");
  const [custom, setCustom] = useState(rangeFor("30d"));

  const range = preset === "custom" ? custom : rangeFor(preset);
  const cid = isCompany ? user?.company_id : activeCompanyId;

  const { data: stats, isLoading } = useQuery({
    queryKey: ["stats", range.start, range.end, cid],
    queryFn: async () =>
      (await api.get("/stats", { params: { company_id: cid, start: range.start, end: range.end } })).data,
    enabled: !!cid,
  });

  const exportCsv = () => {
    if (!stats) return;
    const rows = [
      [t("Metrica"), t("Valore")],
      [t("Vendite totali"), stats.total_sales],
      [t("Numero ordini"), stats.orders_count],
      [t("Ordine medio"), stats.avg_order],
      [t("Agenti attivi"), stats.active_agents],
      [],
      [t("Classifica agenti"), t("Ordini"), t("Fatturato")],
      ...stats.agent_leaderboard.map((a) => [a.agent_name, a.orders, a.revenue]),
      [],
      [t("Prodotti top"), t("Quantità"), t("Fatturato")],
      ...stats.top_products.map((p) => [p.name, p.quantity, p.revenue]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `report_${range.start}_${range.end}.csv`;
    a.click();
    toast.success(t("Report esportato"));
  };

  const kpis = [
    { icon: TrendingUp, label: t("Vendite"), value: eur(stats?.total_sales), accent: "bg-primary/20 text-primary" },
    { icon: ShoppingCart, label: t("Ordini"), value: stats?.orders_count ?? 0, accent: "bg-secondary/20 text-secondary" },
    { icon: Receipt, label: t("Ordine medio"), value: eur(stats?.avg_order), accent: "bg-accent/20 text-accent" },
    isCompany
      ? { icon: Trophy, label: t("Agenti attivi"), value: stats?.active_agents ?? 0, accent: "bg-emerald-500/20 text-emerald-400" }
      : { icon: Wallet, label: t("Provvigione"), value: eur(stats?.my_commission), accent: "bg-emerald-500/20 text-emerald-400", sub: `Aliquota ${stats?.commission_rate ?? 0}%` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Statistiche")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {range.start} → {range.end}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/reports/commissions.pdf?company_id=${cid}&start=${range.start}&end=${range.end}`, "_blank")} className="gap-2" data-testid="commission-report-btn">
            <FileText className="h-4 w-4" /> {t("Report provvigioni")}
          </Button>
          <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="export-csv-btn">
            <Download className="h-4 w-4" /> {t("Esporta CSV")}
          </Button>
        </div>
      </div>

      {/* Period filter */}
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            data-testid={`period-${p.key}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              preset === p.key ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:border-primary/40"
            }`}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-end gap-2 bg-card border border-border rounded-xl p-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("Da")}</Label>
              <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} data-testid="custom-start-input" className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">A</Label>
              <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} data-testid="custom-end-input" className="h-9" />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <motion.div
            key={k.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-card border border-border rounded-2xl p-5"
            data-testid={`stat-kpi-${k.label.toLowerCase()}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono-j uppercase tracking-wider text-muted-foreground">{k.label}</span>
              <div className={`h-8 w-8 rounded-lg grid place-items-center ${k.accent}`}>
                <k.icon className="h-4 w-4" />
              </div>
            </div>
            <p className="font-display text-2xl font-extrabold mt-3">{k.value}</p>
            {k.sub && <p className="text-xs text-muted-foreground mt-1">{k.sub}</p>}
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-semibold mb-4">{t("Fatturato nel periodo")}</h3>
          <div className="h-[260px]">
            {stats?.revenue_trend?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#64748b" fontSize={11} width={48} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => [eur(v), "Fatturato"]} />
                  <Line type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty loading={isLoading} />
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-semibold mb-4">{t("Prodotti più venduti")}</h3>
          <div className="h-[260px]">
            {stats?.top_products?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.top_products.slice(0, 6)} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
                  <XAxis type="number" stroke="#64748b" fontSize={11} />
                  <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={90} tickFormatter={(s) => (s.length > 12 ? s.slice(0, 12) + "…" : s)} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => [eur(v), "Fatturato"]} />
                  <Bar dataKey="revenue" fill="#4F46E5" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty loading={isLoading} />
            )}
          </div>
        </div>
      </div>

      {isCompany && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-accent" /> {t("Classifica agenti")}
          </h3>
          {stats?.agent_leaderboard?.length ? (
            <div className="space-y-2">
              {stats.agent_leaderboard.map((a, i) => (
                <div key={a.agent_id} className="flex items-center gap-4 p-3 rounded-xl bg-muted/20" data-testid={`leaderboard-${i}`}>
                  <span className={`h-8 w-8 grid place-items-center rounded-lg font-bold text-sm ${i === 0 ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{a.agent_name}</p>
                    <p className="text-xs text-muted-foreground">{a.orders} {t("ordini · provv.")} {eur(a.commission)}</p>
                  </div>
                  <span className="font-display font-extrabold text-primary">{eur(a.revenue)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("Nessun dato nel periodo selezionato.")}</p>
          )}
        </div>
      )}
    </div>
  );
}

const Empty = ({ loading }) => (
  <div className="h-full grid place-items-center text-muted-foreground text-sm">
    {loading ? t("Caricamento…") : (
      <span className="flex flex-col items-center gap-2">
        <Package className="h-7 w-7" /> {t("Nessun dato nel periodo")}
      </span>
    )}
  </div>
);
